import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, LogOut, BookOpen, Bell as BellIcon, FileText, Users, ChevronRight, Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = {
  ankuendigung: { label: 'Ankuendigung', color: 'bg-blue-100 text-blue-700', icon: BellIcon },
  gebetsanliegen: { label: 'Gebetsanliegen', color: 'bg-purple-100 text-purple-700', icon: BookOpen },
  dokument: { label: 'Dokument', color: 'bg-green-100 text-green-700', icon: FileText },
  intern: { label: 'Intern', color: 'bg-orange-100 text-orange-700', icon: Shield },
};

const MemberDashboard = () => {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const [memberName, setMemberName] = useState('');
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(true);

  const texts = {
    de: { title: 'Mitgliederbereich', welcome: 'Willkommen', logout: 'Abmelden', noContent: 'Noch keine exklusiven Inhalte vorhanden. Schauen Sie bald wieder vorbei!', exclusive: 'Exklusive Gemeinde-Inhalte' },
    en: { title: 'Members Area', welcome: 'Welcome', logout: 'Logout', noContent: 'No exclusive content yet. Check back soon!', exclusive: 'Exclusive Community Content' },
    fr: { title: 'Espace membres', welcome: 'Bienvenue', logout: 'Deconnexion', noContent: 'Pas encore de contenu exclusif. Revenez bientot!', exclusive: 'Contenu exclusif' },
  };
  const tx = texts[lang] || texts.de;

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('member_token')}`,
  });

  useEffect(() => {
    const token = localStorage.getItem('member_token');
    if (!token) {
      navigate('/mitglieder/login');
      return;
    }
    setMemberName(localStorage.getItem('member_name') || '');

    const fetchContent = async () => {
      try {
        const res = await fetch(`${API_URL}/api/members/content`, { headers: authHeaders() });
        if (res.status === 401) {
          localStorage.removeItem('member_token');
          navigate('/mitglieder/login');
          return;
        }
        const data = await res.json();
        setContents(data.contents || []);
      } catch (err) {
        console.error('Error fetching member content:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('member_token');
    localStorage.removeItem('member_name');
    localStorage.removeItem('member_email');
    navigate('/mitglieder/login');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#0f2440] text-white shadow-lg">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-yellow-300" />
            <div>
              <h1 className="text-lg font-bold">{tx.title}</h1>
              <p className="text-xs text-white/60">{tx.welcome}, {memberName}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="text-sm text-white/70 hover:text-white transition-colors">Startseite</a>
            <button
              onClick={handleLogout}
              data-testid="member-logout-btn"
              className="px-4 py-2 bg-white/10 rounded-lg text-sm font-medium hover:bg-white/20 transition-colors flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              {tx.logout}
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 py-8">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-testid="member-content-heading">
          {tx.exclusive}
        </h2>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-[#0f2440]" />
          </div>
        ) : contents.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <BookOpen className="w-14 h-14 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">{tx.noContent}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {contents.map((item) => {
              const cat = CATEGORIES[item.category] || CATEGORIES.ankuendigung;
              const CatIcon = cat.icon;
              return (
                <div
                  key={item.id}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow overflow-hidden"
                  data-testid={`member-content-${item.id}`}
                >
                  <div className="p-6">
                    <div className="flex items-start gap-4">
                      <div className={`p-2.5 rounded-xl ${cat.color}`}>
                        <CatIcon className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-bold text-gray-900">{item.title}</h3>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${cat.color}`}>
                            {cat.label}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 whitespace-pre-line leading-relaxed">{item.body}</p>
                        <div className="flex items-center gap-4 mt-3 text-xs text-gray-400">
                          {item.author && <span>Von {item.author}</span>}
                          {item.created_at && (
                            <span>{new Date(item.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default MemberDashboard;
